import { QUESTION_TYPES, replaceUnderscoresUpperCase } from "~/common/utils";
import {
  EMPTY_CHOICE,
  QUESTION_CHOICE_SELECTION_METHOD,
  QUESTION_TYPE,
} from "../constants/supplementalQuestions";
import { yesNoChoicesSchema } from "../schemas/supplementalQuestions";
import {
  AddQuestionForm,
  SupplementalQuestion,
  SupplementalQuestionTypes,
} from "../types/supplementalQuestions";

export const isSupplementalYesNoQuestion = (question: SupplementalQuestion) => {
  return (
    question.type === QUESTION_TYPES.supplemental_single_select &&
    yesNoChoicesSchema.safeParse(question.choices).success
  );
};

export const getSupplementalQuestionTypeLabel = (
  question: SupplementalQuestion,
) => {
  const supplementalQuestionTypeToLabel = {
    supplemental_free_text: "Free text",
    supplemental_single_select: "Single select",
    supplemental_multi_select: "Multi select",
  };

  if (isSupplementalYesNoQuestion(question)) {
    return "Yes/No";
  }

  return supplementalQuestionTypeToLabel[question.type] || "Unknown";
};

export const getDefaultValuesForSupplementalQuestionType = (
  type: SupplementalQuestionTypes,
): Partial<AddQuestionForm> => {
  switch (type) {
    case QUESTION_TYPE.MULTIPLE_CHOICE:
      return {
        type,
        text: "",
        choices: Array(2).fill(EMPTY_CHOICE),
        selectionMethod: "single",
      };

    case QUESTION_TYPE.YES_NO:
      return {
        type,
        text: "",
      };

    case QUESTION_TYPE.FREE_TEXT:
    default:
      return {
        type,
        text: "",
      };
  }
};

export const mapInitialDataToFormDefaults = (
  initialData: SupplementalQuestion,
) => {
  if (initialData.type === QUESTION_TYPES.supplemental_free_text) {
    return {
      id: initialData.id,
      type: QUESTION_TYPE.FREE_TEXT,
      text: replaceUnderscoresUpperCase(initialData.text),
    };
  }

  if (isSupplementalYesNoQuestion(initialData)) {
    return {
      id: initialData.id,
      type: QUESTION_TYPE.YES_NO,
      text: replaceUnderscoresUpperCase(initialData.text),
    };
  }

  if (initialData.type === QUESTION_TYPES.supplemental_single_select) {
    return {
      id: initialData.id,
      type: QUESTION_TYPE.MULTIPLE_CHOICE,
      selectionMethod: QUESTION_CHOICE_SELECTION_METHOD.SINGLE,
      text: replaceUnderscoresUpperCase(initialData.text),
      choices:
        initialData.choices.map((choice) => ({
          label: replaceUnderscoresUpperCase(choice),
        })) ?? [],
    };
  }

  if (initialData.type === QUESTION_TYPES.supplemental_multi_select) {
    return {
      id: initialData.id,
      type: QUESTION_TYPE.MULTIPLE_CHOICE,
      selectionMethod: QUESTION_CHOICE_SELECTION_METHOD.MULTIPLE,
      text: replaceUnderscoresUpperCase(initialData.text),
      choices:
        initialData.choices.map((choice) => ({
          label: replaceUnderscoresUpperCase(choice),
        })) ?? [],
    };
  }

  return {
    id: initialData.id,
    type: QUESTION_TYPE.FREE_TEXT,
    text: replaceUnderscoresUpperCase(initialData.text),
  };
};
